import styled, { css } from "styled-components";
import { motion } from "framer-motion";

/* ─── Shell ────────────────────────────────────────────────────────────── */

export const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(4, 8, 18, 0.72);
  backdrop-filter: blur(12px) saturate(160%);
  -webkit-backdrop-filter: blur(12px) saturate(160%);

  /* Mobile: dock to bottom edge so the Card slides up as a bottom sheet.
     backdrop-filter is dropped — too expensive on phones for the
     marginal visual gain. */
  @media (max-width: 700px) {
    padding: 0;
    align-items: flex-end;
    justify-content: center;
    background: rgba(4, 8, 18, 0.62);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    opacity: ${({ $useCssMotion, $mobileOpen }) =>
      $useCssMotion ? ($mobileOpen ? 1 : 0) : 1};
    transition: ${({ $useCssMotion }) =>
      $useCssMotion ? "opacity 220ms ease-out" : "none"};
  }
`;

export const Card = styled(motion.div)`
  position: relative;
  width: min(100%, 720px);
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  display: flex;
  flex-direction: column;
  border-radius: 28px;
  border: 1px solid var(--color-line);
  background:
    radial-gradient(
      120% 80% at 50% -20%,
      rgba(var(--color-brand-300-rgb), 0.22) 0%,
      rgba(var(--color-brand-300-rgb), 0) 60%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-pale-rgb), 0.9) 0%,
      rgba(var(--color-brand-200-rgb), 0.42) 100%
    ),
    rgb(var(--glass-pale-rgb));
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26);
  overflow: hidden;
  isolation: isolate;

  > * {
    position: relative;
  }

  [data-theme="dark"] & {
    background: rgb(22, 36, 62);
    border-color: transparent;
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.16);
  }

  /* Mobile bottom-sheet. Heavy effects (backdrop-filter, gradients,
     wide shadows) are stripped — the sheet is GPU-promoted via
     translateZ(0) so transforms compose without re-rasterizing.
     !important defeats the higher-specificity desktop-dark rule above. */
  @media (max-width: 700px) {
    width: 100%;
    height: auto;
    max-height: 96vh;
    max-height: 96svh;
    border-radius: 22px 22px 0 0;
    border: none;
    background: #ffffff !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    isolation: isolate;
    box-shadow: 0 -10px 24px rgba(0, 0, 0, 0.32);
    will-change: transform;
    transform: ${({ $useCssMotion, $mobileOpen, $dragOffset = 0 }) => {
      if (!$useCssMotion) return "translate3d(0, 0, 0)";
      if (!$mobileOpen) return "translate3d(0, 100%, 0)";
      return `translate3d(0, ${Math.max(0, $dragOffset)}px, 0)`;
    }};
    transition: ${({ $useCssMotion, $isDragging }) =>
      $useCssMotion && !$isDragging
        ? "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)"
        : "none"};
    contain: layout paint style;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    touch-action: manipulation;

    [data-theme="dark"] & {
      background: #0d111a !important;
      box-shadow: 0 -10px 24px rgba(0, 0, 0, 0.55);
    }

    /* Keep the moving sheet cheap to composite on mobile. */
    &::before {
      display: none;
    }
  }
`;

/* Visual grab handle for the mobile bottom sheet. */
export const DragHandle = styled.div`
  display: none;

  @media (max-width: 700px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 10px 0 4px;
    pointer-events: auto;
    user-select: none;
    touch-action: none;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }

    &:focus-visible::before {
      outline: 2px solid var(--color-brand-500);
      outline-offset: 4px;
    }

    &::before {
      content: "";
      width: 44px;
      height: 4px;
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.18);
    }

    [data-theme="dark"] &::before {
      background: rgba(255, 255, 255, 0.36);
    }
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 20px 24px 14px;

  /* Plans view on mobile uses just the drag-handle; picker view shows
     the bar with Back + close. */
  @media (max-width: 700px) {
    display: ${({ $showOnMobile }) => ($showOnMobile ? "flex" : "none")};
    padding: 6px 16px 10px;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-strong);

  @media (max-width: 700px) {
    font-size: 18px;
  }
`;

export const CloseButton = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 45%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  &:hover {
    transform: translateY(-1px) scale(1.04);
  }

  svg {
    width: 14px;
    height: 14px;
    position: relative;
    z-index: 1;
    path {
      stroke: var(--color-brand-700);
    }
  }

  [data-theme="dark"] & svg path {
    stroke: var(--color-text-strong);
  }

  @media (max-width: 700px) {
    background: var(--color-surface-2);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    box-shadow: 0 3px 8px rgba(var(--color-brand-500-rgb), 0.12);
  }
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 0 18px;
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  @media (max-width: 700px) {
    gap: 4px;
    padding: 0 0 10px;
    /* Body must own the vertical scroll on the bottom sheet — without
       this the PlanCard (and its Buy button) gets clipped by the
       Card's max-height/overflow:hidden on shorter phones. */
    flex: 1 1 auto;
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 0;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

/* ─── Carousel ─────────────────────────────────────────────────────────── */

const SLIDE_WIDTH_PCT = 66;
const SLIDE_GAP_PX = 6;
const SIDE_PEEK_PCT = (100 - SLIDE_WIDTH_PCT) / 2;

const MOBILE_SLIDE_WIDTH_PCT = 78;
const MOBILE_SIDE_PEEK_PCT = (100 - MOBILE_SLIDE_WIDTH_PCT) / 2;

export const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
`;

/* Native horizontal scroll-snap viewport. Snap behaves correctly on
   touch + trackpad; JS only handles arrow/dot clicks and reads
   scrollLeft to derive the active index. */
export const CarouselViewport = styled.div`
  position: relative;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  padding: 18px 0 28px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
  /* Horizontal touch is ours; vertical bubbles to the bottom-sheet's
     drag-to-close on the Card. */
  touch-action: pan-x;

  @media (max-width: 700px) {
    padding: 14px 0 22px;
  }
`;

export const CarouselTrack = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${SLIDE_GAP_PX}px;
`;

/* Edge spacers — half the leftover width so a centered slide has
   somewhere to scroll into. Plain percentages so layout is stable
   from frame 1 (no JS measurement round-trip). */
export const CarouselSpacer = styled.div`
  flex: 0 0 ${SIDE_PEEK_PCT}%;

  @media (max-width: 700px) {
    flex: 0 0 ${MOBILE_SIDE_PEEK_PCT}%;
  }
`;

export const CarouselSlide = styled.div`
  flex: 0 0 ${SLIDE_WIDTH_PCT}%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  box-sizing: border-box;
  scroll-snap-align: center;
  /* Scale + opacity are written by the JS scroll listener every frame;
     no CSS transition here on purpose (CSS transitions on $active flip
     would fight the per-frame interpolation). */
  will-change: transform, opacity;
  transform-origin: 50% 50%;

  @media (max-width: 700px) {
    flex: 0 0 ${MOBILE_SLIDE_WIDTH_PCT}%;
    will-change: auto;
  }
`;

export const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  ${({ $side }) => ($side === "left" ? "left: 12px;" : "right: 12px;")}
  transform: translateY(-50%);
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 86px;
  width: auto;
  height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  color: var(--color-text-body);
  cursor: pointer;
  box-shadow: var(--shadow-micro);
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  transition:
    transform 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.45);
  }

  @media (max-width: 700px) {
    display: none;
  }

  &:hover {
    transform: translateY(-50%) scale(1.06);
  }

  svg {
    width: 16px;
    height: 16px;
    path {
      stroke: var(--color-text-strong);
    }
  }
`;

export const ArrowButtonLabel = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1;
  white-space: nowrap;
`;

export const DotsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 2px 0 12px;

  @media (max-width: 700px) {
    padding: 0 0 8px;
  }
`;

export const Dot = styled.button`
  width: ${({ $active }) => ($active ? "20px" : "8px")};
  height: 8px;
  border-radius: 999px;
  border: none;
  padding: 0;
  background: ${({ $active }) =>
    $active ? "var(--color-brand-500)" : "var(--color-line-strong)"};
  cursor: pointer;
  transition:
    width 240ms cubic-bezier(0.22, 1, 0.36, 1),
    background 240ms cubic-bezier(0.22, 1, 0.36, 1);
`;

/* ─── Plan card ────────────────────────────────────────────────────────── */

export const PlanCard = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 460px;
  padding: 24px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  border-radius: 22px;
  color: var(--color-text-strong);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--color-brand-300-rgb), 0.18) 0%,
      rgba(var(--color-brand-300-rgb), 0) 70%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.92) 100%
    );
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  /* Border keeps a fixed 1px width across popular/non-popular so the
     swap doesn't reflow — the brand-blue accent comes from the ring
     shadow below. */
  border: 1px solid white;
  box-shadow: ${({ $popular }) =>
    $popular
      ? `0 0 0 2px rgba(var(--color-brand-400-rgb), 0.32),
         0 6px 20px -8px rgba(var(--color-brand-500-rgb)),
         0 1px 4px rgba(var(--color-brand-500-rgb), 0.1),
         inset 0 1px 0 rgba(255, 255, 255, 0.55)`
      : `0 6px 14px -10px rgba(var(--color-brand-500-rgb), 0.18),
         0 1px 4px rgba(var(--color-brand-500-rgb), 0.06),
         inset 0 1px 0 rgba(255, 255, 255, 0.55)`};
  transition:
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  /* Inner content above the glow layer; PopularBadge has its own
     higher z-index so it sits above everything. */
  > * {
    position: relative;
    z-index: 1;
  }

  [data-theme="dark"] & {
    background:
      radial-gradient(
        120% 60% at 30% -10%,
        rgba(var(--color-brand-700-rgb), 0.28) 0%,
        rgba(var(--color-brand-700-rgb), 0) 65%
      ),
      linear-gradient(
        180deg,
        rgba(20, 30, 52, 0.95) 0%,
        rgba(12, 18, 34, 0.97) 100%
      );

    border-color: ${({ $popular }) =>
      $popular
        ? "rgba(var(--color-brand-400-rgb), 0.6)"
        : "rgba(var(--color-brand-400-rgb), 0.14)"};
    box-shadow: ${({ $popular }) =>
      $popular
        ? `0 0 0 1px rgba(var(--color-brand-400-rgb), 0.28),
           0 6px 18px -8px rgba(var(--color-brand-500-rgb), 0.4),
           0 2px 6px rgba(0, 0, 0, 0.32),
           inset 0 -8px 18px rgba(0, 0, 0, 0.28)`
        : `0 10px 22px -12px rgba(0, 0, 0, 0.55),
           0 3px 8px rgba(0, 0, 0, 0.32),
           inset 0 -8px 18px rgba(0, 0, 0, 0.24)`};
  }

  @media (max-width: 700px) {
    padding: 22px 20px 18px;
    gap: 14px;
    border-radius: 20px;
    background: var(--color-surface-2);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    box-shadow: ${({ $popular }) =>
      $popular
        ? `0 0 0 1px rgba(var(--color-brand-400-rgb), 0.28),
           0 6px 14px rgba(var(--color-brand-500-rgb), 0.14)`
        : `0 4px 12px rgba(var(--color-brand-500-rgb), 0.1)`};
    transition: none;

    [data-theme="dark"] & {
      background: var(--color-surface-3);
      box-shadow: ${({ $popular }) =>
        $popular
          ? `0 0 0 1px rgba(var(--color-brand-400-rgb), 0.26),
             0 6px 14px rgba(0, 0, 0, 0.24)`
          : `0 4px 12px rgba(0, 0, 0, 0.2)`};
    }
  }
`;

/* Sibling that owns the heavily-blurred brand "lamp" disc — clipped to
   the rounded panel without forcing PlanCard itself to clip its top
   sticker badge. */
export const PlanCardGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;

  &::before {
    content: "";
    position: absolute;
    right: -80px;
    top: -80px;
    width: 256px;
    height: 256px;
    border-radius: 50%;
    background: ${({ $popular }) =>
      $popular
        ? "rgba(var(--color-brand-500-rgb), 0.36)"
        : "rgba(var(--color-brand-500-rgb), 0.2)"};
    filter: blur(56px);
  }

  [data-theme="dark"] &::before {
    background: ${({ $popular }) =>
      $popular
        ? "rgba(var(--color-brand-400-rgb), 0.4)"
        : "rgba(var(--color-brand-400-rgb), 0.24)"};
  }

  @media (max-width: 700px) {
    display: none;
  }
`;

export const PopularBadge = styled.span`
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 12px;
  border-radius: 999px;
  font-family: var(--font-family-primary);
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;
  z-index: 3;
  box-shadow: 0 4px 12px rgba(var(--color-brand-500-rgb), 0.28);

  ${({ $variant }) =>
    $variant === "solid" &&
    css`
      border: 1px solid rgba(var(--color-brand-500-rgb), 0.65);
      background: var(--color-brand-500);
      color: #ffffff;
    `}

  ${({ $variant }) =>
    (!$variant || $variant === "soft") &&
    css`
      border: 1px solid rgba(var(--color-brand-400-rgb), 0.5);
      background: var(--color-brand-200);
      color: var(--color-brand-700);
      [data-theme="dark"] & {
        background: var(--color-brand-800, #0e2a4c);
        color: var(--color-brand-300);
      }
    `}

  ${({ $variant }) =>
    $variant === "muted" &&
    css`
      border: 1px solid var(--color-line);
      background: var(--color-surface-2);
      color: var(--color-text-muted);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
      [data-theme="dark"] & {
        background: var(--color-surface-3);
        color: var(--color-text-body);
      }
    `}
`;

export const CardHero = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 0 12px;
  border-bottom: 1px solid var(--color-line);

  @media (max-width: 700px) {
    padding: 0 0 10px;
    gap: 8px;
  }
`;

export const CardHeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

export const CardHeroValue = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
`;

export const CardHeroValueNumber = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 38px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;

  @media (max-width: 700px) {
    font-size: 36px;
  }
`;

export const CardHeroValueLabel = styled.span`
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 900;
  letter-spacing: -0.005em;

  @media (max-width: 700px) {
    font-size: 15px;
  }
`;

export const CardHeroSubtitle = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.005em;

  @media (max-width: 700px) {
    font-size: 13px;
  }
`;

export const CardHeroBonus = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  /* padding: 3px 10px; */
  border-radius: 999px;
  /* border: 1px solid rgba(var(--color-brand-400-rgb), 0.32); */
  /* background: color-mix(
    in srgb,
    var(--color-brand-500) 14%,
    var(--glass-pale, #e2f2ff)
  ); */
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 2px;
  letter-spacing: 0.01em;
  font-variant-numeric: tabular-nums;

  [data-theme="dark"] & {
    background: color-mix(in srgb, var(--color-brand-400) 20%, transparent);
    color: var(--color-brand-200);
  }

  @media (max-width: 700px) {
    font-size: 12px;
  }
`;

export const CardHeroIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  color: var(--color-brand-600);

  [data-theme="dark"] & {
    color: var(--color-brand-300);
  }

  svg {
    display: block;
    filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.05));
  }

  @media (max-width: 700px) {
    svg {
      filter: none;
    }
  }
`;

export const FeaturesList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: 700px) {
    gap: 7px;
  }
`;

export const Feature = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

/* premium → gold star, check → brand tick, missing → muted X */
export const FeatureIcon = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  font-size: 14px;
  font-weight: 800;
  line-height: 1;
  color: ${({ $tier }) => {
    if ($tier === "premium") return "#f59e0b";
    if ($tier === "missing") return "var(--color-text-dim)";
    return "var(--color-brand-600)";
  }};
  filter: ${({ $tier }) =>
    $tier === "premium"
      ? "drop-shadow(0 1px 3px rgba(245, 158, 11, 0.4))"
      : "none"};

  [data-theme="dark"] & {
    color: ${({ $tier }) => {
      if ($tier === "premium") return "#fbbf24";
      if ($tier === "missing") return "var(--color-text-dim)";
      return "var(--color-brand-300)";
    }};
  }

  @media (max-width: 700px) {
    filter: none;
  }
`;

export const FeatureLabel = styled.span`
  color: ${({ $tier }) =>
    $tier === "missing" ? "var(--color-text-dim)" : "var(--color-text-strong)"};
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: ${({ $tier }) => ($tier === "premium" ? 700 : 500)};
  letter-spacing: -0.005em;
  line-height: 1.35;

  @media (max-width: 700px) {
    font-size: 14px;
  }
`;

export const CardFooterInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;

  @media (max-width: 700px) {
    margin-top: 2px;
  }
`;

export const CardFooterLine = styled.p`
  margin: 0;
  color: ${({ $accent }) =>
    $accent ? "var(--color-text-body)" : "var(--color-text-muted)"};
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: ${({ $accent }) => ($accent ? 600 : 500)};
  line-height: 1.35;
  letter-spacing: -0.005em;

  @media (max-width: 700px) {
    font-size: 12.5px;
  }
`;

/* ─── Card actions ─────────────────────────────────────────────────────── */

export const ActionArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: auto;
`;

export const BuyButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  border: none;
  /* Kill iOS double-tap-zoom / 300ms tap delay so the button responds
     on the first tap even right after a horizontal carousel swipe. */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.32) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  box-shadow:
    0 12px 24px rgba(var(--color-brand-600-rgb), 0.36),
    0 3px 8px rgba(var(--color-brand-600-rgb), 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow:
      0 16px 30px rgba(var(--color-brand-600-rgb), 0.46),
      0 4px 10px rgba(var(--color-brand-600-rgb), 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.6),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.36);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.55),
      0 3px 8px rgba(0, 0, 0, 0.32),
      inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  }

  @media (max-width: 700px) {
    height: 50px;
    box-shadow: 0 8px 16px rgba(var(--color-brand-600-rgb), 0.2);
  }
`;

export const BuyAmount = styled.span`
  color: rgba(255, 255, 255, 0.92);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

export const CryptoLink = styled.button`
  align-self: center;
  height: 28px;
  padding: 0 10px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  border-radius: 999px;
  transition:
    color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy);

  &:hover:not(:disabled) {
    color: var(--color-brand-700);
    background: rgba(var(--color-brand-300-rgb), 0.16);
  }

  [data-theme="dark"] &:hover:not(:disabled) {
    color: var(--color-brand-300);
    background: rgba(var(--color-brand-700-rgb), 0.28);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* ─── Footnote ─────────────────────────────────────────────────────────── */

export const Footnote = styled.p`
  margin: 0;
  text-align: center;
  font-family: var(--font-family-primary);
  font-size: 12px;
  color: var(--color-text-muted);
  padding: 10px 18px 4px;

  @media (max-width: 700px) {
    font-size: 11.5px;
    padding: 8px 14px 4px;
  }
`;

/* ─── Step 2: payment-method picker ────────────────────────────────────── */

export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px 6px 0;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  border-radius: 8px;
  transition: color 180ms var(--ease-snappy);

  &:hover {
    color: var(--color-text-strong);
  }

  svg {
    width: 14px;
    height: 14px;
    path {
      stroke: currentColor;
    }
  }

  @media (max-width: 700px) {
    font-size: 12px;
  }
`;

/* Collapsible plan summary block — header always visible, details
   slide open via grid-template-rows. */
export const PlanSummary = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 18px 10px;
  border-radius: 16px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  overflow: hidden;

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.16);
    background: var(--color-surface-3);
  }

  @media (max-width: 700px) {
    margin: 0 14px 8px;
    border-radius: 14px;
  }
`;

export const PlanSummaryHeader = styled.button`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  width: 100%;
  cursor: pointer;
  text-align: left;
  border-radius: inherit;
  transition: background 180ms var(--ease-snappy);

  &:hover {
    background: rgba(var(--color-brand-300-rgb), 0.06);
  }

  [data-theme="dark"] &:hover {
    background: rgba(var(--color-brand-400-rgb), 0.06);
  }

  &:focus-visible {
    outline: 2px solid var(--color-brand-500);
    outline-offset: -2px;
  }

  @media (max-width: 700px) {
    padding: 12px 14px;
    gap: 12px;
  }
`;

export const PlanSummaryChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  margin-left: 4px;
  color: var(--color-text-muted);
  transition: transform 220ms var(--ease-snappy);
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0deg)")};

  svg {
    width: 16px;
    height: 16px;
    display: block;
  }
`;

export const PlanSummaryExpansion = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition:
    grid-template-rows 280ms var(--ease-snappy),
    opacity 220ms var(--ease-snappy);

  > div {
    overflow: hidden;
  }
`;

export const PlanSummaryExpansionInner = styled.div`
  padding: 12px 16px 14px;
  border-top: 1px solid var(--color-line);
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: -0.005em;

  [data-theme="dark"] & {
    border-top-color: rgba(var(--color-brand-400-rgb), 0.14);
  }

  @media (max-width: 700px) {
    padding: 12px 14px;
    font-size: 12.5px;
  }
`;

export const PlanSummaryIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: var(--color-brand-500);
  background: rgba(var(--color-brand-400-rgb), 0.14);

  [data-theme="dark"] & {
    color: var(--color-brand-300);
    background: rgba(var(--color-brand-400-rgb), 0.18);
  }

  svg {
    display: block;
    filter: drop-shadow(0 2px 4px rgba(var(--color-brand-700-rgb), 0.32));
  }

  @media (max-width: 700px) {
    svg {
      filter: none;
    }
  }
`;

export const PlanSummaryText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const PlanSummaryDrops = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.1;
`;

export const PlanSummaryName = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: -0.005em;
`;

export const PlanSummaryPrice = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;

  @media (max-width: 700px) {
    font-size: 16px;
  }
`;

export const PaymentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0 18px;
  padding: 16px 16px 14px;
  border-radius: 18px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.16);
    background: var(--color-surface-3);
  }

  @media (max-width: 700px) {
    margin: 0 14px;
    padding: 14px 14px 12px;
    border-radius: 16px;
    gap: 12px;
  }
`;

export const PaymentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 700px) {
    gap: 8px;
  }
`;

export const PaymentMethodCard = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  height: 84px;
  padding: 12px 12px 10px;
  border-radius: 14px;
  border: 2px solid
    ${({ $selected }) =>
      $selected ? "var(--color-brand-500)" : "var(--color-line)"};
  background: ${({ $selected }) =>
    $selected ? "rgba(var(--color-brand-300-rgb), 0.12)" : "white"};
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  transition:
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy),
    transform 180ms var(--ease-snappy);

  &:hover:not(:disabled) {
    border-color: ${({ $selected }) =>
      $selected ? "var(--color-brand-500)" : "var(--color-line-strong)"};
    transform: translateY(-1px);
  }

  [data-theme="dark"] & {
    border-color: ${({ $selected }) =>
      $selected
        ? "var(--color-brand-400)"
        : "rgba(var(--color-brand-400-rgb), 0.18)"};
    background: ${({ $selected }) =>
      $selected
        ? "rgba(var(--color-brand-700-rgb), 0.32)"
        : "var(--color-surface-2)"};
  }

  @media (max-width: 700px) {
    height: 76px;
    padding: 10px 10px 8px;
    border-radius: 12px;
    gap: 6px;
  }
`;

export const MethodBadge = styled.span`
  position: absolute;
  top: -7px;
  right: -7px;
  padding: 3px 6px;
  border-radius: 999px;
  font-family: var(--font-family-primary);
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.02em;
  transform: rotate(12deg);
  pointer-events: none;
  /* box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25); */

  ${({ $variant }) =>
    $variant === "discount" &&
    css`
      background: #10b981;
      color: #022c22;
    `}

  ${({ $variant }) =>
    $variant === "fee" &&
    css`
      background: #f59e0b;
      color: #44260a;
    `}
`;

export const LockBadge = styled.span`
  position: absolute;
  top: -7px;
  right: -7px;
  padding: 3px 7px;
  border-radius: 999px;
  background: var(--color-surface-3);
  border: 1px solid var(--color-line);
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  transform: rotate(8deg);
  pointer-events: none;
`;

export const MethodIcons = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  color: var(--color-text-strong);

  svg {
    height: 20px;
    width: auto;
    display: block;
  }

  /* Stacked secondary marks slightly smaller — pair reads as balanced
     row, not hero + subordinate. */
  svg + svg {
    height: 18px;
  }

  @media (max-width: 700px) {
    height: 20px;
    svg {
      height: 18px;
    }
    svg + svg {
      height: 16px;
    }
  }
`;

export const MethodLabel = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 700px) {
    font-size: 11.5px;
  }
`;

export const SecureNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.4;

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.14);
    background: var(--color-surface-2);
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--color-text-dim);
  }

  @media (max-width: 700px) {
    padding: 10px 12px;
    font-size: 12px;
    gap: 8px;
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const TelegramGuide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-1);

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.14);
    background: var(--color-surface-2);
  }

  @media (max-width: 700px) {
    padding: 12px 14px;
    gap: 10px;
  }
`;

export const TelegramGuideHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.4;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--color-text-dim);
  }

  @media (max-width: 700px) {
    font-size: 12px;
    gap: 8px;
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const TelegramGuidePreviews = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

export const TelegramGuideThumb = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.14);
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${({ $position }) => $position || "center top"};
  }
`;

export const TelegramGuideFooter = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.4;

  a {
    color: var(--color-brand-500);
    text-decoration: underline;
    text-underline-offset: 2px;
    font-weight: 700;
    transition: color 180ms var(--ease-snappy);

    &:hover {
      color: var(--color-brand-600);
    }
  }

  @media (max-width: 700px) {
    font-size: 12px;
  }
`;

export const PayNowButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 48px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  border: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.32) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.005em;
  cursor: pointer;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  box-shadow:
    0 12px 24px rgba(var(--color-brand-600-rgb), 0.36),
    0 3px 8px rgba(var(--color-brand-600-rgb), 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.55),
      0 3px 8px rgba(0, 0, 0, 0.32),
      inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  }

  @media (max-width: 700px) {
    height: 50px;
    box-shadow: 0 8px 16px rgba(var(--color-brand-600-rgb), 0.2);
  }
`;
